import { IssueType } from '../../database/issue.interface';
import type { IssueNoteEntity } from '../../entity/issue-note.entity';
import type { IssueEntity } from '../../entity/issue.entity';

/**
 * Portal Issue DTOs (PD-13705, PD-13773)
 *
 * Contract for the storefront "Submit an Issue" widget — a signed-in
 * customer sends a categorized inquiry that lands as an `issue` record
 * in the CRM, linked to their active client — and for the follow-up
 * visibility surface: the customer's own submitted issues (list +
 * detail + communication thread with the support team).
 */

/**
 * Categories a portal customer may pick from. Deliberately a subset of
 * `IssueType` — `Accounting` is an internal triage category and is not
 * offered to customers.
 */
export const PORTAL_ISSUE_TYPES: ReadonlyArray<IssueType> = [
	IssueType.QUESTION,
	IssueType.REQUEST,
	IssueType.PROBLEM,
];

/** Server-enforced field limits, shared so the widget can mirror them. */
export const PORTAL_ISSUE_SUBJECT_MAX_LENGTH = 255;
export const PORTAL_ISSUE_MESSAGE_MAX_LENGTH = 10000;

export interface PortalSubmitIssueRequestDto {
	/** Becomes the issue name. */
	subject: string;
	/** One of PORTAL_ISSUE_TYPES; maps to issue.type. */
	type: IssueType;
	/** Becomes the issue description. */
	message: string;
}

export interface PortalSubmitIssueResponseDto {
	/** The created issue id — shown to the customer as their reference number. */
	issueId: number;
}

// --- Visibility surface (PD-13773) ---

/**
 * One row in the customer's "Support requests" list. Strictly scoped to
 * issues the signed-in portal user submitted themselves
 * (`issue.fk_public_user__id_submitter` = current public user).
 */
export type PortalIssueListItemDto = Pick<
	IssueEntity,
	'id' | 'name' | 'type' | 'status' | 'createdDate' | 'statusUpdatedDatetime'
> & {
	/**
	 * Portal-visible messages on the thread (customer replies + staff notes
	 * shared with the customer). Excludes the original description.
	 */
	messageCount: number;
};

export interface PortalIssueListResponseDto {
	issues: Array<PortalIssueListItemDto>;
}

/**
 * One entry of the customer↔support communication thread. The original
 * submission message is emitted as the first `customer` entry; later
 * entries come from `issue_note` rows that are portal-visible.
 */
export type PortalIssueMessageDto = Pick<IssueNoteEntity, 'createdDate'> & {
	id: number;
	body: string;
	/** Who wrote it — drives the chat-style alignment in the portal UI. */
	author: 'customer' | 'support';
	/** Display name (customer's own name, or the staff member's). */
	authorName: string | null;
};

export type PortalIssueDetailDto = Pick<
	IssueEntity,
	'id' | 'name' | 'type' | 'status' | 'createdDate' | 'statusUpdatedDatetime'
> & {
	thread: Array<PortalIssueMessageDto>;
};

export interface PortalIssueReplyRequestDto {
	/** The customer's reply; becomes a portal-visible issue note. */
	message: string;
}

export interface PortalIssueReplyResponseDto {
	/** The refreshed thread after the reply was appended. */
	detail: PortalIssueDetailDto;
}
